import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid } from '@mui/material';
import {
  MenuBook as MenuIcon,
  LocalShipping as DeliveryIcon,
  PointOfSale as CashDeskIcon,
  TableBar as RestaurantIcon,
  AccountBalanceWallet as CashBookIcon,
  Tune as ProgramIcon,
  Receipt as DatevIcon,
  Campaign as MarketingIcon,
  Payment as PaymentIcon,
  Language as WebShopIcon,
  MonitorHeart as KitchenMonitorIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

interface SettingsTile {
  key: string;
  translationKey: string;
  fallbackLabel: string;
  icon: React.ReactNode;
  path: string | null; // null = coming soon
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const tiles: SettingsTile[] = [
    {
      key: 'goods_menu',
      translationKey: 'settings.goods_menu',
      fallbackLabel: 'Goods / menu / settings',
      icon: <MenuIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'delivery',
      translationKey: 'settings.delivery_settings',
      fallbackLabel: 'Delivery settings',
      icon: <DeliveryIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'cash_desk',
      translationKey: 'settings.cash_desk_settings',
      fallbackLabel: 'Cash desk settings',
      icon: <CashDeskIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'restaurant',
      translationKey: 'settings.restaurant_settings',
      fallbackLabel: 'Restaurant settings',
      icon: <RestaurantIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'cash_book',
      translationKey: 'settings.cash_book_settings',
      fallbackLabel: 'Cash book / Settings',
      icon: <CashBookIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'program',
      translationKey: 'settings.program_settings',
      fallbackLabel: 'Program settings',
      icon: <ProgramIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: '/settings/program',
    },
    {
      key: 'datev',
      translationKey: 'settings.datev',
      fallbackLabel: 'DateV',
      icon: <DatevIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'marketing',
      translationKey: 'settings.marketing',
      fallbackLabel: 'Marketing',
      icon: <MarketingIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'payment_methods',
      translationKey: 'settings.payment_methods',
      fallbackLabel: 'Payment methods',
      icon: <PaymentIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'webshop',
      translationKey: 'settings.webshop',
      fallbackLabel: 'WebShop',
      icon: <WebShopIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'kitchen_monitor',
      translationKey: 'settings.kitchen_monitor',
      fallbackLabel: 'Kitchen monitor',
      icon: <KitchenMonitorIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, pb: { xs: 4, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid item xs={6} sm={4} md={3} key={tile.key}>
            <Box
              onClick={() => {
                if (tile.path) navigate(tile.path);
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                py: 4,
                px: 2,
                borderRadius: '12px',
                backgroundColor: tile.path ? '#2a2d42 !important' : '#252838 !important',
                border: '1px solid #3a3d52',
                cursor: tile.path ? 'pointer' : 'default',
                opacity: tile.path ? 1 : 0.5,
                minHeight: 140,
                transition: 'all 0.15s ease',
                '&:hover': tile.path
                  ? {
                      backgroundColor: '#33364d !important',
                      border: '1px solid #00C896',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                    }
                  : {},
              }}
            >
              {tile.icon}
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: '#e0e0e0 !important',
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}
              >
                {t(tile.translationKey) || tile.fallbackLabel}
              </Typography>
              {!tile.path && (
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    color: '#6b7280 !important',
                    fontStyle: 'italic',
                  }}
                >
                  {t('settings.coming_soon') || 'Coming soon'}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SettingsPage;
